import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tooltip } from '@trevor/ui-components';
import { equals } from 'ramda';
import { safeOnChange } from '@/util/helpers/event-helpers';
import { datasourceUrlPrefix, userAction } from '@/util/helpers/ajax-helpers';
import { SINGLE_VALUE } from '@/components/ChartHolder.constants';
import DataTableHeading from '../../util/DataTableHeading';
import { changeDataPresentation, layerUserInteraction } from '../../actions';
import { ChartToggle } from '../ChartToggle';
import ChartFormattingOptions, {
  PIVOT_CUSTOM_HEADINGS,
  PIVOT_SHOW_TOTALS,
  POST_TEXT_KEY,
} from '../ChartFormattingOptions';
import ChartHolder from '../ChartHolder';
import { DATE, DATETIME, FLOAT, INT, INTERVAL } from '../../util/ColumnType';
import css from '../../css/module/charting-config.css';
import flex from '../../css/module/flex.css';
import AjaxSpinner from '../AjaxSpinner';
import LabelledToggle from '../LabelledToggle';
import ChartColorSelect from '../ChartColorSelect';
import {
  buildDatasets,
  rgbToHex,
  secondColIsDimension,
  transformDatatableIfNecessary,
} from '../../util/helpers/charting-helpers';
import ModalTip from '../ModalTip';
import { cellFormatter } from '../../util/helpers/chart-formatting-helpers';
import {
  DAY,
  MONTH,
  WEEK,
  YEAR,
} from '../../util/enums/EnumDateTimeAggregation';
import {
  COMPARE_PERIODS_EXPLAINER,
  CUMULATIVE_EXPLAINER,
  ROW_INDEX_EXPLAINER,
  SHOW_TREND_LABEL_EXPLAINER,
} from '../../util/Explainers';
import {
  CHART_CONFIG_ONBOARDING,
  COLUMNS_SUBTOTALS_TOGGLE,
  ROWS_SUBTOTALS_TOGGLE,
} from '../../constants/test-ids';
import HighlightFeature, { LEFT } from '../HighlightFeature';
import {
  BAR_DISPLAY,
  GUAGE_DISPLAY,
  PERCENTAGE,
  RAW_VALUE,
} from '../ChartSingleValue';
import PivotColumnsRowsSelection, {
  PIVOT_COLUMNS_ID,
  PIVOT_ROWS_ID,
} from '../PivotColumnsRowsSelection';
import {
  BAR,
  BARLINE,
  LINE,
  PIE,
  PIVOT,
  STACKED_CHART,
  TABLE,
} from './chartType.constants';
import {
  SECTION_COLORS,
  SECTION_FORMATTING,
  CONFIG_FLIP_DIMS,
  CONFIG_COMPARE_PERIODS,
  CONFIG_SHOW_PERCENT,
  CONFIG_SHOW_DATA_LABELS,
  CONFIG_ROTATE_DATA_LABELS,
  CONFIG_MAX_LEGEND_ITEMS,
  CONFIG_SHOW_LEGEND,
  CONFIG_IS_DONUT,
  CONFIG_CUMULATIVE,
  CONFIG_FILL_MISSING_VALUES,
  CONFIG_REVERSE_X_AXIS,
  CONFIG_LOGARITHMIC,
  CONFIG_SHOW_Y_AXIS_LABEL,
  CONFIG_Y_AXIS_MAX,
  CONFIG_Y_AXIS_LABEL,
  CONFIG_Y_AXIS_MIN,
  CONFIG_2ND_Y_AXIS,
  CONFIG_2ND_Y_AXIS_LABEL,
  CONFIG_2ND_Y_AXIS_MAX,
  CONFIG_2ND_Y_AXIS_MIN,
  CONFIG_TARGET_COLOR,
  CONFIG_TARGET,
  CONFIG_ROW_INDEX,
  CONFIG_FONT_SIZE,
  CONFIG_HIDE_DATE,
  CONFIG_SHOW_GROWTH,
  CONFIG_DISPLAY_AS,
  CONFIG_GAUGE,
  CONFIG_TARGET_VALUE,
  DEFAULT_PROGRESS_COLOR,
  CONFIG_FORMATTING,
  CONFIG_COLOR,
  PROGRESS_LABEL,
} from './chartingConfiguration.constants';

// colors

let scrollPos = 0;

const SUBTOTALS_SUPPORTED_COLUMN_TYPES = ['INT', 'FLOAT'];

export const isPivotColumnTypeSubtotalsSupported = (headings) => {
  if (!headings) return false;

  const pivotColumnType = headings[headings.length - 1].columnType().type();

  return SUBTOTALS_SUPPORTED_COLUMN_TYPES.includes(pivotColumnType);
};

class ChartingConfiguration extends Component {
  constructor(props) {
    super(props);
    this.state = {
      colorOpened: null, // the color that is currently opened by the user
      targetColorOpen: false,
      dataTable: null, // copy of the dataTable
    };
    this.activePresentation = this.activePresentation.bind(this);
    this.onUpdateFormat = this.onUpdateFormat.bind(this);
    this.getChartingOption = this.getChartingOption.bind(this);
    this.setChartingOption = this.setChartingOption.bind(this);
    this.onColumnsRowsUpdate = this.onColumnsRowsUpdate.bind(this);
  }

  componentDidMount() {
    const { dataTable } = this.props;
    // we save a copy of the dataTable in the state so that, if the user clicks "Load more" results, the labels and formatting + colors sections, that rely on the dataTable, still appear (rather than showing a loader).
    if (dataTable) {
      this.setState({
        dataTable,
      });
    }
  }

  componentDidUpdate(prevProps) {
    // update the locally stored datatable with the new one when it loads
    if (
      (!prevProps.dataTable && this.props.dataTable) ||
      (this.props.dataTable &&
        prevProps.dataTable.rows().length !==
          this.props.dataTable.rows().length) ||
      (this.props.dataTable &&
        !equals(
          prevProps.dataTable.headings(),
          this.props.dataTable.headings(),
        ))
    ) {
      this.setState({
        dataTable: this.props.dataTable,
      });
    }
  }

  activePresentation() {
    const { headings, dataPresentation, dataTable } = this.props;
    const presentation = ChartToggle.parsePresentation(
      headings,
      dataPresentation,
      dataTable,
    );
    return presentation;
  }

  getChartingOption(subSection) {
    const { chartingOptions } = this.props;

    const section = subSection[0];
    const item = subSection[1];
    const sectionObj = chartingOptions[section] || {};
    const result = sectionObj[item];
    // undefined is used to control default value
    if (result === undefined && subSection.length > 2) {
      return subSection[2];
    }
    return result;
  }

  setChartingOption(subSection, value, recordAction, minValue = null) {
    const { onUpdateChartingOptions, chartingOptions, isLite } = this.props;

    // prevent Lite plan users from changing charting options (just in case they delete the element covering the chart settings);
    if (isLite || (minValue !== null && value !== '' && value < minValue)) {
      return;
    }

    const [section, item] = subSection;
    onUpdateChartingOptions({
      ...chartingOptions,
      [section]: {
        ...chartingOptions[section],
        [item]: value,
      },
    });

    if (recordAction) {
      // just recording toggles (as input would trigger an action for every typed letter)
      const action =
        section === SECTION_COLORS
          ? 'use-chart-setting-color'
          : `use-chart-setting-${item}`;
      userAction(action);
    }
  }

  getFormattingOptions() {
    const { chartingOptions } = this.props;
    return chartingOptions[SECTION_FORMATTING] || {};
  }

  onUpdateFormat(heading, keyToUpdate, value, recordAction) {
    // take a copy of the existing object for a column name (returns an empty obj if none exists)
    const existingObj = this.getChartingOption([SECTION_FORMATTING, heading]);
    // take a copy
    const newObj = { ...existingObj };
    // add or update the key with the value
    newObj[keyToUpdate] = value;
    const subSection = [SECTION_FORMATTING, heading];
    this.setChartingOption(subSection, newObj);
    if (recordAction) {
      // record action here instead of in setChartingOption function as we want to use the keyToUpdate as the name and not the column header
      userAction(`use-chart-formatting-${keyToUpdate.replace(/\_/g, '-')}`);
    }
  }

  /**
   * Function that executes when the configuration for rows and columns is updated for pivot table presentation
   */
  onColumnsRowsUpdate(columns, rows) {
    const { chartingOptions, onUpdateChartingOptions } = this.props;

    const delta = {
      ...chartingOptions,
      [PIVOT_CUSTOM_HEADINGS]: {
        columns,
        rows,
      },
    };
    onUpdateChartingOptions(delta);

    userAction('use-chart-pivot-columns-rows');
  }

  /**
   * Function that executes when the configuration for rows and columns is updated for pivot table presentation
   */
  onShowTotalsUpdate(newValue, section) {
    const { chartingOptions, onUpdateChartingOptions } = this.props;

    const delta = {
      ...chartingOptions,
      [PIVOT_SHOW_TOTALS]: {
        ...chartingOptions[PIVOT_SHOW_TOTALS],
        [section]: newValue,
      },
    };
    onUpdateChartingOptions(delta);

    userAction('use-chart-pivot-show-totals');
  }

  firstColumnIsDate() {
    const { headings } = this.props;
    return [DATE, DATETIME].includes(headings[0].columnType().type());
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  secondColCanBeMetric() {
    const { headings } = this.props;
    return [INT, FLOAT].includes(headings[1].columnType().type());
  }

  secondColIsDim() {
    const { headings } = this.props;
    return secondColIsDimension(
      headings,
      this.getChartingOption,
      this.activePresentation(),
    );
  }

  dimensionsSection() {
    const { headings } = this.props;

    if (
      ![BAR, LINE, PIE, PIVOT, BARLINE, STACKED_CHART].includes(
        this.activePresentation(),
      )
    ) {
      return null; // don't show Dimensions section
    }

    const flipDims = this.getChartingOption(CONFIG_FLIP_DIMS);

    // const onToggleSecondColIsDim = () => {
    // 	this.setChartingOption(CONFIG_2ND_COL_IS_DIM, !this.secondColIsDim());
    // }

    const dims = [headings[0]]; // dimensions always includes first column
    if (headings.length === 3 && this.secondColIsDim()) {
      dims.push(headings[1]); // second column is also a dimension
    }
    if (dims.length === 2 && flipDims) {
      const firstCol = dims[0];
      dims[0] = dims[1];
      dims[1] = firstCol;
    }

    return (
      <div>
        {dims.length !== 2 ? null : (
          <div>
            <LabelledToggle
              value={flipDims}
              onChange={(newValue) => this.toggleFlipDimensions(newValue)}
            >
              {' '}
              Flip dimensions
            </LabelledToggle>
          </div>
        )}
      </div>
    );
  }

  toggleFlipDimensions(newValue) {
    this.setChartingOption(CONFIG_FLIP_DIMS, newValue, true);
    this.unToggleComparePeriods(newValue);
  }

  // when turning on settings that are incompatible with compareperiods (e.g. flip dims and cumulative) and that also don't appear when the compareperiods appears, turn off compareperiods.
  unToggleComparePeriods(newValue) {
    if (newValue && this.getChartingOption(CONFIG_COMPARE_PERIODS)) {
      this.setChartingOption(CONFIG_COMPARE_PERIODS, false);
    }
  }

  canComparePeriods(showCompareToggle) {
    const { dataTable, dataPresentation } = this.props;
    return ChartHolder.canComparePeriods(
      dataTable,
      dataPresentation,
      showCompareToggle,
    );
  }

  dims() {
    const { headings } = this.props;

    return this.canComparePeriods() &&
      this.getChartingOption(CONFIG_COMPARE_PERIODS)
      ? 2
      : headings.slice(0, this.secondColIsDim() ? 2 : 1);
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  metrics() {
    const { headings } = this.props;
    return headings.slice(this.dims().length);
  }

  togglePercentages(turningOn) {
    const { headings } = this.props;
    // toggle percentage on/off
    this.setChartingOption(CONFIG_SHOW_PERCENT, turningOn, true);

    // if turning percentages on and data labels are not shown, toggle them on so that percentages show
    if (turningOn && !this.getChartingOption(CONFIG_SHOW_DATA_LABELS)) {
      this.setChartingOption(CONFIG_SHOW_DATA_LABELS, true, true);
    }
    // apply/remove % from formatting, as appropriate
    const metricColumn = headings[headings.length - 1].columnName().toString();
    const configKey = CONFIG_FORMATTING(metricColumn);
    const formatting = this.getChartingOption(configKey) || {};
    const postText = formatting[POST_TEXT_KEY];
    if (turningOn && !postText) {
      this.setChartingOption(configKey, {
        ...formatting,
        [POST_TEXT_KEY]: '%',
      });
    } else if (!turningOn && postText === '%') {
      this.setChartingOption(configKey, { ...formatting, [POST_TEXT_KEY]: '' });
    }
  }

  metricsSection() {
    if (
      ![BAR, LINE, PIE, PIVOT, BARLINE, STACKED_CHART].includes(
        this.activePresentation(),
      )
    ) {
      return null; // don't show Metrics section
    }

    return (
      <div>
        <div>
          <LabelledToggle
            value={this.getChartingOption(CONFIG_SHOW_DATA_LABELS)}
            onChange={(newValue) =>
              this.setChartingOption(CONFIG_SHOW_DATA_LABELS, newValue, true)
            }
          >
            {' '}
            Show value labels
          </LabelledToggle>
        </div>
        {[PIE, STACKED_CHART].includes(this.activePresentation()) && (
          <LabelledToggle
            value={this.getChartingOption(CONFIG_SHOW_PERCENT)}
            onChange={(newValue) => this.togglePercentages(newValue)}
          >
            {' '}
            Show as percentages
          </LabelledToggle>
        )}
        {!this.getChartingOption(CONFIG_SHOW_DATA_LABELS) ? null : (
          <div>
            <LabelledToggle
              value={this.getChartingOption(CONFIG_ROTATE_DATA_LABELS)}
              onChange={(newValue) =>
                this.setChartingOption(
                  CONFIG_ROTATE_DATA_LABELS,
                  newValue,
                  true,
                )
              }
            >
              {' '}
              Rotate value labels
            </LabelledToggle>
          </div>
        )}
      </div>
    );
  }

  toggleComparePeriods(newValue) {
    this.setChartingOption(CONFIG_COMPARE_PERIODS, newValue, true);
    // if flip dims is turned on, turn it off (e.g. because this setting was available and the user turned it on, but then modified the query so it's not available but compareperiods is)
    if (newValue && this.getChartingOption(CONFIG_FLIP_DIMS)) {
      this.setChartingOption(CONFIG_FLIP_DIMS, false);
    }
  }

  chartSettings() {
    const { headings } = this.props;

    if (this.activePresentation() === TABLE) {
      return (
        <div
          className={css.section}
          data-remove-after-refactoring="chart-configuration-section"
        >
          <h4>Chart settings</h4>
          <div className={css.subSection}>
            {this.buildInput('Max rows', CONFIG_MAX_LEGEND_ITEMS, 5, 'number')}
          </div>
        </div>
      );
    }
    if (!this.dimensionsSection() && !this.metricsSection()) {
      return null;
    }
    if (this.activePresentation() === PIVOT) {
      return null;
    }

    const compareToggle = (
      <LabelledToggle
        value={this.getChartingOption(CONFIG_COMPARE_PERIODS)}
        onChange={safeOnChange((newValue) =>
          this.toggleComparePeriods(newValue),
        )}
        disabled={this.disableComparePeriods()}
      >
        <span className={flex.flex}>
          Compare different {this.periodToCompare(headings)}
          <ModalTip title="Compare periods" body={COMPARE_PERIODS_EXPLAINER} />
        </span>
      </LabelledToggle>
    );

    return (
      <div
        className={css.section}
        data-remove-after-refactoring="chart-configuration-section"
      >
        <h4>Chart settings</h4>
        <div className={css.subSection}>
          {this.dimensionsSection()}
          {this.metricsSection()}
          <div>
            <LabelledToggle
              value={this.getChartingOption(CONFIG_SHOW_LEGEND)}
              onChange={(newValue) =>
                this.setChartingOption(CONFIG_SHOW_LEGEND, newValue, true)
              }
            >
              {' '}
              Show legend
            </LabelledToggle>
          </div>
          {!this.isPie() ? null : (
            <div>
              <LabelledToggle
                value={this.getChartingOption(CONFIG_IS_DONUT)}
                onChange={(newValue) =>
                  this.setChartingOption(CONFIG_IS_DONUT, newValue, true)
                }
              >
                {' '}
                Show as donut
              </LabelledToggle>
            </div>
          )}
          {this.buildInput(
            `Max ${
              this.dims().length === 2 ||
              this.isPie() ||
              (this.canComparePeriods() &&
                this.getChartingOption(CONFIG_COMPARE_PERIODS))
                ? 'legend items'
                : 'data points'
            }`,
            CONFIG_MAX_LEGEND_ITEMS,
            5,
            'number',
            this.activePresentation() === PIE ||
              ([BAR, LINE, PIE, STACKED_CHART].includes(
                this.activePresentation(),
              ) &&
                this.dims().length === 2)
              ? 12
              : '',
          )}

          {this.canComparePeriods(true) ? (
            this.disableComparePeriods() ? (
              <div>
                <div data-tooltip-id="disableCompare">{compareToggle}</div>
                <Tooltip id="disableCompare">
                  <span>
                    {[WEEK, YEAR].includes(headings[0].aggregation())
                      ? 'To use this, change the date dimension in your Summarize step to DAY or MONTH'
                      : this.getChartingOption(CONFIG_CUMULATIVE)
                      ? "Untoggle 'Cumulative total' to use this"
                      : "Untoggle 'Include missing dates' to use this"}
                  </span>
                </Tooltip>
              </div>
            ) : (
              compareToggle
            )
          ) : null}
        </div>
      </div>
    );
  }

  disableComparePeriods() {
    return (
      !this.canComparePeriods() ||
      this.getChartingOption(CONFIG_CUMULATIVE) ||
      this.getChartingOption(CONFIG_FILL_MISSING_VALUES)
    );
  }

  periodToCompare(headings) {
    const agg = headings[0].aggregation();
    switch (agg) {
      case DAY:
        return 'months';
      case MONTH:
        return 'years';
      default:
        return 'periods';
    }
  }

  axisFormatting() {
    return (
      <label
        style={{ margin: '10px 0' }}
        className={`${flex.justifyContentBetween} full-width ${flex.alignItemsCenter}`}
      >
        <span style={{ fontWeight: 'normal' }}>Axis formatting</span>
        <span style={{ fontWeight: 'normal', color: '#999' }}>
          (see "Labels and formatting" below)
        </span>
      </label>
    );
  }

  xAxisSection() {
    const { headings } = this.props;
    if (
      ![BAR, LINE, PIE, BARLINE, STACKED_CHART].includes(
        this.activePresentation(),
      )
    ) {
      return null; // don't show x-axis section
    }
    if (this.isPie()) {
      return null; // only show x-axis for stacked charts (not pie charts)
    }

    const includeMissingDatesToggle = (
      <div>
        <LabelledToggle
          value={this.getChartingOption(CONFIG_FILL_MISSING_VALUES)}
          onChange={(newValue) => this.toggleAddMissingDates(newValue)}
          disabled={
            this.canComparePeriods() &&
            this.getChartingOption(CONFIG_COMPARE_PERIODS)
          }
        >
          Include missing dates
        </LabelledToggle>
      </div>
    );

    return (
      <div
        className={css.section}
        data-remove-after-refactoring="chart-configuration-section"
      >
        <h4>X-axis</h4>
        <div className={css.subSection}>
          <div>
            <LabelledToggle
              value={this.getChartingOption(CONFIG_REVERSE_X_AXIS)}
              onChange={(newValue) =>
                this.setChartingOption(CONFIG_REVERSE_X_AXIS, newValue, true)
              }
            >
              {' '}
              Reverse x-axis
            </LabelledToggle>
            {this.axisFormatting()}
          </div>

          {![DATE, DATETIME].includes(
            headings[this.getChartingOption(CONFIG_FLIP_DIMS) ? 1 : 0]
              .columnType()
              .type(),
          ) ? null : this.canComparePeriods() &&
            this.getChartingOption(CONFIG_COMPARE_PERIODS) ? (
            <div>
              <div data-tooltip-id="disableAddDates">
                {includeMissingDatesToggle}
              </div>
              <Tooltip id="disableAddDates">
                Untoggle 'Compare different {this.periodToCompare(headings)}' to
                use this
              </Tooltip>
            </div>
          ) : (
            <div>{includeMissingDatesToggle}</div>
          )}
        </div>
      </div>
    );
  }

  toggleAddMissingDates(newValue) {
    this.setChartingOption(CONFIG_FILL_MISSING_VALUES, newValue, true);
    this.unToggleComparePeriods(newValue);
  }

  isPie() {
    return this.activePresentation() === PIE && this.dims().length === 1;
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  isStacked() {
    return this.activePresentation() === PIE && this.dims().length === 2;
  }

  yAxisSection() {
    const { headings, dataPresentation } = this.props;
    if (
      ![BAR, LINE, PIE, BARLINE, STACKED_CHART].includes(
        this.activePresentation(),
      )
    ) {
      return null; // don't show y-axis 1 section
    }
    if (this.isPie()) {
      return null; // only show y-axis for stacked charts (not pie charts)
    }

    const canHave2ndYAxis = ChartHolder.canHave2ndYAxis(headings);

    const cumulativeToggle = (
      <div>
        <LabelledToggle
          value={this.getChartingOption(CONFIG_CUMULATIVE)}
          onChange={safeOnChange((newValue) => this.toggleCumulative(newValue))}
          disabled={
            this.canComparePeriods() &&
            this.getChartingOption(CONFIG_COMPARE_PERIODS)
          }
        >
          <span className={flex.flex}>
            Cumulative total
            <ModalTip
              title="Display cumulative total"
              body={CUMULATIVE_EXPLAINER}
            />
          </span>
        </LabelledToggle>
      </div>
    );

    return (
      <div
        className={css.section}
        data-remove-after-refactoring="chart-configuration-section"
      >
        <h4>Y-axis</h4>
        <div className={css.subSection}>
          {this.firstColumnIsDate() &&
          headings.length === 2 &&
          [BAR, LINE].includes(this.activePresentation()) ? (
            this.canComparePeriods() &&
            this.getChartingOption(CONFIG_COMPARE_PERIODS) ? (
              <div>
                <div data-tooltip-id="disableCumulative">
                  {cumulativeToggle}
                </div>
                <Tooltip id="disableCumulative">
                  Untoggle 'Compare different {this.periodToCompare(headings)}'
                  to use this
                </Tooltip>
              </div>
            ) : (
              <div>{cumulativeToggle}</div>
            )
          ) : null}
          <div>
            <LabelledToggle
              value={this.getChartingOption(CONFIG_LOGARITHMIC)}
              onChange={(newValue) =>
                this.setChartingOption(CONFIG_LOGARITHMIC, newValue, true)
              }
            >
              {' '}
              Logarithmic scale
            </LabelledToggle>
          </div>
          <div>
            <LabelledToggle
              value={this.getChartingOption(CONFIG_SHOW_Y_AXIS_LABEL)}
              onChange={(newValue) =>
                this.setChartingOption(CONFIG_SHOW_Y_AXIS_LABEL, newValue, true)
              }
            >
              {' '}
              Label y-axis
            </LabelledToggle>
          </div>

          {this.axisFormatting()}

          {!this.getChartingOption(CONFIG_SHOW_Y_AXIS_LABEL)
            ? null
            : this.buildInput(
                'Axis label',
                CONFIG_Y_AXIS_LABEL,
                'count',
                'text',
              )}

          {this.getChartingOption(CONFIG_LOGARITHMIC)
            ? null
            : this.buildInput('Axis max', CONFIG_Y_AXIS_MAX, 100, 'number')}

          {this.getChartingOption(CONFIG_LOGARITHMIC)
            ? null
            : this.buildInput('Axis min', CONFIG_Y_AXIS_MIN, -10, 'number')}

          {this.dims().length !== 1 ||
          !canHave2ndYAxis ||
          dataPresentation === BARLINE ? null : (
            <div>
              <LabelledToggle
                disabled={!canHave2ndYAxis}
                value={this.getChartingOption(CONFIG_2ND_Y_AXIS)}
                onChange={(newValue) =>
                  this.setChartingOption(CONFIG_2ND_Y_AXIS, newValue, true)
                }
              >
                {' '}
                2nd y-axis
              </LabelledToggle>
            </div>
          )}

          {this.getChartingOption(CONFIG_SHOW_Y_AXIS_LABEL) &&
          (dataPresentation === BARLINE ||
            (this.getChartingOption(CONFIG_2ND_Y_AXIS) &&
              this.dims().length === 1 &&
              canHave2ndYAxis))
            ? this.buildInput(
                '2nd axis label',
                CONFIG_2ND_Y_AXIS_LABEL,
                'count',
                'text',
              )
            : null}

          {(dataPresentation === BARLINE ||
            (this.getChartingOption(CONFIG_2ND_Y_AXIS) &&
              this.dims().length === 1 &&
              canHave2ndYAxis)) &&
          !this.getChartingOption(CONFIG_LOGARITHMIC)
            ? this.buildInput(
                '2nd axis max',
                CONFIG_2ND_Y_AXIS_MAX,
                100,
                'number',
              )
            : null}

          {(dataPresentation === BARLINE ||
            (this.getChartingOption(CONFIG_2ND_Y_AXIS) &&
              this.dims().length === 1 &&
              canHave2ndYAxis)) &&
          !this.getChartingOption(CONFIG_LOGARITHMIC)
            ? this.buildInput(
                '2nd axis min',
                CONFIG_2ND_Y_AXIS_MIN,
                -10,
                'number',
              )
            : null}
        </div>
      </div>
    );
  }

  toggleCumulative(newValue) {
    this.setChartingOption(CONFIG_CUMULATIVE, newValue, true);
    // if newValue is true and compare periods is true, change compare periods to false.
    // Both can be true at the same time if the user, for instance, activates the compare
    // toggle when the agg is DAY, then changes agg to WEEK (so the compare feature is disabled)
    // then activates cumulative, then switches back to agg back to DAY.
    this.unToggleComparePeriods(newValue);
  }

  colorsSection() {
    const { dataPresentation, dataTable } = this.props;

    if (
      ![BAR, LINE, PIE, BARLINE, STACKED_CHART].includes(
        this.activePresentation(),
      )
    ) {
      return null; // don't show colors section section
    }
    if (!dataTable) {
      // not enough data yet
      return (
        <div
          className={css.section}
          data-remove-after-refactoring="chart-configuration-section"
        >
          <h4>
            <span>Colors</span>
          </h4>
          <AjaxSpinner small />
        </div>
      );
    }

    // transform table as per toggled flipped dims / comparing periods settings etc.
    const dt = transformDatatableIfNecessary(
      dataTable,
      this.getChartingOption.bind(this),
      dataPresentation,
    );

    const { xAxis, datasets } = buildDatasets(
      dt,
      dataPresentation,
      this.dims().length,
      this.getChartingOption.bind(this),
    );

    const labels = [];
    const addLabel = (originalLabel, label, bgColor, i) => {
      const rgba = bgColor.match(/rgba\((.*)\)/)[1].split(',');
      const hex = rgbToHex(rgba[0], rgba[1], rgba[2]);
      const onChangeColor = (_hex) => {
        this.setChartingOption(CONFIG_COLOR(originalLabel), _hex, true);
      };
      const colorSelectRow = (
        <ChartColorSelect
          key={i}
          label={label}
          hex={hex}
          onChangeColor={onChangeColor}
          colorOpened={this.state.colorOpened === i}
          setColorOpened={() =>
            this.setState({
              colorOpened: this.state.colorOpened === i ? null : i,
            })
          }
          index={i}
        />
      );
      labels.push([label, colorSelectRow]);
    };

    const formatValue = cellFormatter(dataTable, this.getChartingOption, 0);

    datasets.map((d, index) => {
      if (Array.isArray(d.backgroundColor)) {
        xAxis.map((label, i) => {
          const originalLabel =
            dataPresentation === PIE ? label : d.extras.originalLabel;
          const formattedLabel =
            dataPresentation === PIE ? formatValue(label) : label;
          addLabel(originalLabel, formattedLabel, d.backgroundColor[i], i);
        });
      } else {
        addLabel(
          d.extras.originalLabel,
          d.label || d.extras.originalLabel,
          d.backgroundColor,
          index,
        );
      }
    });

    const sortedLabels = labels.sort((a, b) => {
      if (!a[0]) {
        return 1;
      }
      if (!b[0]) {
        return -1;
      }
      return `${a[0]}`.localeCompare(`${b[0]}`);
    });

    return (
      <div
        className={css.section}
        data-remove-after-refactoring="chart-configuration-section"
      >
        <h4>
          <span>Colors</span>
        </h4>
        <div className={css.subSection}>
          {sortedLabels.map((label) => label[1])}
        </div>
      </div>
    );
  }

  buildInput(
    label,
    chartOption,
    placeholder,
    type,
    defaultValue = '',
    minValue,
    modalTip,
  ) {
    // only use default value when chart option is null, as when it's an empty string it means user has cleared the value, so we should show an empty string
    const inputValue = this.getChartingOption(chartOption) ?? defaultValue;

    return (
      <div
        className={`${css.formatOptionRow} ${flex.alignItemsCenter} margin-top-10 margin-bottom-10 ${flex.justifyContentBetween}`}
      >
        <div className="margin-right-10" style={{ textAlign: 'right' }}>
          {label}
          {modalTip || null}
        </div>
        <div>
          <input
            type={type || 'text'}
            value={inputValue}
            style={{ marginRight: '0px' }}
            className="form-control set-input-height"
            onChange={(e) =>
              this.setChartingOption(
                chartOption,
                e.target.value,
                null,
                minValue,
              )
            }
            placeholder={`e.g. ${placeholder}`}
            onBlur={() => userAction(`use-chart-setting-${chartOption[1]}`)}
          />
        </div>
      </div>
    );
  }

  singleValueOptions() {
    const { headings } = this.props;

    const canBeSingleValueWithTrend =
      ChartHolder.canBeSingleValueWithTrend(headings);

    if (this.activePresentation() !== SINGLE_VALUE) {
      return null;
    }

    const onChangeColor = (hex) => {
      this.setChartingOption(CONFIG_TARGET_COLOR, hex);
    };

    const colorStyle = { padding: '0px' };

    const disableProgressBarToggle =
      headings.length === 1 &&
      ![INT, FLOAT].includes(headings[0].columnType().type());

    const progressBarToggle = (
      <LabelledToggle
        disabled={disableProgressBarToggle}
        value={this.getChartingOption(CONFIG_TARGET)}
        onChange={(newValue) =>
          this.setChartingOption(CONFIG_TARGET, newValue, true)
        }
      >
        Show progress bar
      </LabelledToggle>
    );

    return (
      <div
        className={css.section}
        data-remove-after-refactoring="chart-configuration-section"
      >
        <h4>Chart settings</h4>
        <div className={css.subSection}>
          {canBeSingleValueWithTrend
            ? this.buildInput(
                'Row to use',
                CONFIG_ROW_INDEX,
                2,
                'number',
                1,
                1,
                <ModalTip title="Row to use" body={ROW_INDEX_EXPLAINER} />,
              )
            : null}
          <div
            className={`${css.formatOptionRow} ${flex.alignItemsCenter} ${flex.justifyContentBetween}`}
          >
            <div className="margin-right-10" style={{ textAlign: 'right' }}>
              Font size
            </div>
            <div>
              <select
                style={{ marginRight: '0px' }}
                value={this.getChartingOption(CONFIG_FONT_SIZE) || 35}
                className="form-control set-input-height"
                onChange={(e) =>
                  this.setChartingOption(CONFIG_FONT_SIZE, e.target.value)
                }
              >
                {[20, 25, 30, 35, 40, 45, 50, 60].map((item, i) => (
                  <option key={i} value={item}>
                    {item}px
                  </option>
                ))}
              </select>
            </div>
          </div>
          {canBeSingleValueWithTrend ? (
            <div className={css.rowItem} style={{ paddingRight: '10px' }}>
              <LabelledToggle
                value={this.getChartingOption(CONFIG_HIDE_DATE) ?? false}
                disabled={!canBeSingleValueWithTrend}
                onChange={(newValue) =>
                  this.setChartingOption(CONFIG_HIDE_DATE, newValue, true)
                }
              >
                Hide date values
              </LabelledToggle>
            </div>
          ) : null}
        </div>
        <div
          className={`${css.subSection} position-relative ${css.trendSection}`}
        >
          <h4>Trend</h4>
          {!canBeSingleValueWithTrend ? (
            <div className={`${css.warning} margin-top-15 ${css.info}`}>
              Want to show a trend label (e.g. +4%)? &nbsp;
              <ModalTip
                title="Show a trend label"
                body={SHOW_TREND_LABEL_EXPLAINER}
              >
                <span style={{ textDecoration: 'underline' }}>Learn how</span>
              </ModalTip>
            </div>
          ) : null}
          {canBeSingleValueWithTrend ? (
            <div className={css.rowItem} style={{ paddingRight: '10px' }}>
              <LabelledToggle
                value={
                  !canBeSingleValueWithTrend
                    ? false
                    : this.getChartingOption(CONFIG_SHOW_GROWTH) === undefined
                    ? true
                    : this.getChartingOption(CONFIG_SHOW_GROWTH)
                }
                disabled={!canBeSingleValueWithTrend}
                onChange={(newValue) =>
                  this.setChartingOption(CONFIG_SHOW_GROWTH, newValue, true)
                }
              >
                Show trend label
              </LabelledToggle>
            </div>
          ) : null}
          {(this.getChartingOption(CONFIG_SHOW_GROWTH) ||
            !this.getChartingOption(CONFIG_SHOW_GROWTH)) &&
          canBeSingleValueWithTrend ? (
            <div className="w-100">
              <div
                className={`${css.rowItem} ${css.formatOptionRow} ${flex.alignItemsCenter} ${flex.justifyContentBetween}`}
              >
                <div
                  className="margin-right-10"
                  style={{
                    textAlign: 'right',
                    color: canBeSingleValueWithTrend ? 'inherit' : '#aaa',
                    cursor: canBeSingleValueWithTrend
                      ? 'default'
                      : 'not-allowed',
                  }}
                >
                  Display as
                </div>
                <div>
                  <select
                    style={{ marginRight: '0px' }}
                    disabled={!canBeSingleValueWithTrend}
                    value={
                      this.getChartingOption(CONFIG_DISPLAY_AS) || PERCENTAGE
                    }
                    className="form-control set-input-height"
                    onChange={(e) =>
                      this.setChartingOption(CONFIG_DISPLAY_AS, e.target.value)
                    }
                  >
                    {[PERCENTAGE, RAW_VALUE].map((item, i) => (
                      <option key={i} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <div className={css.subSection}>
          <h4>Progress bar</h4>
          <div className={css.rowItem} style={{ paddingRight: '10px' }}>
            {disableProgressBarToggle ? (
              <div className="w-100">
                <div data-tooltip-id="disableProgress">{progressBarToggle}</div>
                <Tooltip id="disableProgress">Value must be a number</Tooltip>
              </div>
            ) : (
              <div className="w-100">{progressBarToggle}</div>
            )}
          </div>
          {disableProgressBarToggle ||
          !this.getChartingOption(CONFIG_TARGET) ? null : (
            <div>
              <div
                className={`${css.rowItem} ${css.formatOptionRow} ${flex.alignItemsCenter} ${flex.justifyContentBetween}`}
              >
                <div
                  className="margin-right-10"
                  style={{ textAlign: 'right', color: 'inherit' }}
                >
                  Display as
                </div>
                <div>
                  <select
                    style={{ marginRight: '0px' }}
                    disabled={headings[0].columnType().type() === INTERVAL}
                    value={
                      this.getChartingOption(CONFIG_GAUGE) || GUAGE_DISPLAY
                    }
                    className="form-control set-input-height"
                    onChange={(e) =>
                      this.setChartingOption(CONFIG_GAUGE, e.target.value)
                    }
                  >
                    {[GUAGE_DISPLAY, BAR_DISPLAY].map((item, i) => (
                      <option key={i} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {this.buildInput(
                'Your target',
                CONFIG_TARGET_VALUE,
                5000,
                'number',
              )}
              <div
                className={`${css.rowItem} full-width`}
                style={{ paddingRight: '10px' }}
              >
                <ChartColorSelect
                  key={PROGRESS_LABEL}
                  label={PROGRESS_LABEL}
                  hex={
                    this.getChartingOption(CONFIG_TARGET_COLOR) ||
                    DEFAULT_PROGRESS_COLOR
                  }
                  onChangeColor={onChangeColor}
                  colorOpened={this.state.targetColorOpen}
                  setColorOpened={() =>
                    this.setState({
                      targetColorOpen: !this.state.targetColorOpen,
                    })
                  }
                  style={colorStyle}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  formattingSection() {
    const { dataPresentation } = this.props;

    const { dataTable } = this.state;

    const newDT = transformDatatableIfNecessary(
      dataTable,
      this.getChartingOption.bind(this),
      dataPresentation,
    );

    return (
      <div
        className={css.section}
        data-remove-after-refactoring="chart-configuration-section"
      >
        <h4>Labels & formatting</h4>
        <div className={css.subSection}>
          {!dataTable ? (
            <AjaxSpinner small />
          ) : (
            <ChartFormattingOptions
              headings={newDT.headings()}
              onUpdateFormat={this.onUpdateFormat}
              onUpdateLabel={this.setChartingOption}
              state={this.getFormattingOptions()}
              layers={this.props.layers}
              selectedLayerId={this.props.selectedLayerId}
              getLabel={this.getChartingOption}
              activePresentation={this.activePresentation()}
            />
          )}
        </div>
      </div>
    );
  }

  pivotColumnsAndRowsSection() {
    if (this.activePresentation() !== PIVOT) {
      return null;
    }

    const { dataTable, chartingOptions } = this.props;

    const getShowTotals = (type) =>
      chartingOptions[PIVOT_SHOW_TOTALS]
        ? chartingOptions[PIVOT_SHOW_TOTALS][type]
        : false;

    const shouldTotalsBeDisabled = (criteria) =>
      !isPivotColumnTypeSubtotalsSupported(dataTable?.headings?.()) ||
      (chartingOptions[PIVOT_CUSTOM_HEADINGS] &&
        chartingOptions[PIVOT_CUSTOM_HEADINGS][criteria].length === 0);

    return (
      <div>
        <div
          className={css.section}
          data-remove-after-refactoring="chart-configuration-section"
        >
          <h4>Pivot settings</h4>
          <div className={css.subSection}>
            <PivotColumnsRowsSelection
              customHeadings={chartingOptions[PIVOT_CUSTOM_HEADINGS]}
              dataTable={dataTable}
              onColumnsRowsUpdate={this.onColumnsRowsUpdate}
            />
          </div>
        </div>
        <div
          className={css.section}
          data-remove-after-refactoring="chart-configuration-section"
        >
          <h4>Subtotals</h4>
          <div className={css.subSection}>
            <div
              data-tooltip-id="disabledMessageColumns"
              data-testid={COLUMNS_SUBTOTALS_TOGGLE}
            >
              <LabelledToggle
                value={getShowTotals(PIVOT_COLUMNS_ID)}
                onChange={(newValue) =>
                  this.onShowTotalsUpdate(newValue, 'columns')
                }
                disabled={shouldTotalsBeDisabled(PIVOT_ROWS_ID)}
              >
                Sum column values
              </LabelledToggle>
            </div>
            <div
              data-tooltip-id="disabledMessageRows"
              data-testid={ROWS_SUBTOTALS_TOGGLE}
            >
              <LabelledToggle
                value={getShowTotals(PIVOT_ROWS_ID)}
                onChange={(newValue) =>
                  this.onShowTotalsUpdate(newValue, 'rows')
                }
                disabled={shouldTotalsBeDisabled(PIVOT_COLUMNS_ID)}
              >
                Sum row values
              </LabelledToggle>
            </div>
          </div>
          {shouldTotalsBeDisabled(PIVOT_ROWS_ID) && (
            <Tooltip id="disabledMessageColumns" place="top">
              <div style={{ maxWidth: '230px' }}>
                {!isPivotColumnTypeSubtotalsSupported(dataTable?.headings?.())
                  ? 'The final column in your results table should be a number column to show subtotals'
                  : 'At least one column option is needed to show subtotals'}
              </div>
            </Tooltip>
          )}
          {shouldTotalsBeDisabled(PIVOT_COLUMNS_ID) && (
            <Tooltip id="disabledMessageRows" place="top">
              <div style={{ maxWidth: '230px' }}>
                {!isPivotColumnTypeSubtotalsSupported(dataTable?.headings?.())
                  ? 'The final column in your results table should be a number column to show subtotals'
                  : 'At least one column option is needed to show subtotals'}
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  handleClickUpgradeButton() {
    window.open(`${datasourceUrlPrefix()}/billing`, '_blank');
    userAction('click-upgrade-in-chart-settings');
  }

  render() {
    const { isLite } = this.props;

    const onScroll = (e) => {
      //
      // This component is wrapped in <Resizable> which sets scrollTop to 0 whenever resizing.
      //   We don't want that to happen (as loses scroll position) so when it does, we reset it back to its previous position.
      //
      if (e.currentTarget.scrollTop === 0 && scrollPos > 200) {
        // reset back to previous position
        e.currentTarget.scrollTop = scrollPos;
      } else {
        // store current position
        scrollPos = e.currentTarget.scrollTop;
      }
    };

    const { headings, showOnboarding } = this.props;

    if (!headings) {
      return <AjaxSpinner />;
    }

    const isLiteClass = isLite ? css.isLiteClass : '';

    const onboardingElement = (
      <HighlightFeature
        position={LEFT}
        text={
          <span data-testid={CHART_CONFIG_ONBOARDING}>
            Easily <b>configure</b> and <b>customize</b> your chart using the
            options in the sidebar.
          </span>
        }
        targetAction="customize-chart"
        delay={3500}
      />
    );

    return (
      <div
        className={`${css.sidebarInner} position-relative ${isLiteClass}`}
        onScroll={onScroll}
      >
        {showOnboarding ? onboardingElement : null}
        {!isLite ? null : (
          <div className={css.sidebarCover}>
            <div className={css.siderbarCoverInner} />
            <div
              className={`${flex.column} ${flex.alignItemCenter}`}
              style={{ zIndex: 1 }}
            >
              <Button onClick={this.handleClickUpgradeButton}>
                <FontAwesomeIcon icon="gamepad" />
                &nbsp; Upgrade to Pro
              </Button>
              <p className="text-align-center margin-top-15">
                Get advanced charting options
                <br />
                and more.
              </p>
            </div>
          </div>
        )}

        {this.chartSettings()}
        {this.xAxisSection()}
        {this.yAxisSection()}
        {this.colorsSection()}
        {this.singleValueOptions()}
        {this.pivotColumnsAndRowsSection()}
        {this.formattingSection()}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    chartingOptions: props.chartingOptions || state.layers.chartingOptions,
    dataTable: props.dataTable || state.rows.dataTable,
    headings:
      props.headings ||
      (state.rows.dataTable // if datatable is available, use its headings
        ? state.rows.dataTable.headings()
        : !state.rows.headings
        ? null
        : state.rows.headings.map((c) => DataTableHeading.fromColumn(c))),
    dataPresentation: props.dataPresentation || state.rows.dataPresentation,
    isLite: state.datasource.isLite,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    onUpdateChartingOptions:
      ownProps.onUpdateChartingOptions ||
      ((newChartingOptions) => {
        dispatch(
          layerUserInteraction({
            event: 'update-charting-options',
            chartingOptions: newChartingOptions,
          }),
        );
      }),
    onChangeDataPresentation: (presentation) => () => {
      dispatch(changeDataPresentation(presentation));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ChartingConfiguration);
